import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFreelancerProfile } from '@/hooks/queries/useProfile';
import {
  hasValidIdentityProof,
  requiresDriverLicense,
  isDriverLicenseDocumentValid,
  isDriverLicenseBackDocumentValid,
  DRIVER_LICENSE_FRONT_CATEGORY,
  DRIVER_LICENSE_BACK_CATEGORY,
  isRcInsuranceValid,
  isNdaActivityReceiptValid,
  isTaxRegularityAttestationValid,
  isCriminalRecordValid,
  requiresNdaActivityReceipt,
  requiresTaxRegularityAttestation,
  requiresRcInsurance,
  requiresCriminalRecord,
  requiresCv,
  requiresPortfolio,
  requiresDiplomas,
  hasValidCv,
  hasValidPortfolio,
  hasValidDiplomas,
  requiresWorkLocationAnswer,
  hasAnsweredWorkLocation,
  requiresResidencePermit,
  isResidencePermitValid,
  isKYCComplete,
} from '@/utils/kycValidation';
import {
  getRequiredPortageKycCategories,
  normalizePortageKycCategory,
} from '@portage-kyc';
import { validateSwiftBic } from '@/utils/swiftBicValidation';

/**
 * Hook pour valider les prérequis du freelance (documents et informations bancaires)
 * Utilise la même logique que isKYCComplete pour vérifier la validité des documents
 * (pas seulement leur existence, mais aussi validation_status et dates d'expiration)
 * @param {boolean} isFreelancer - Si l'utilisateur est un freelance
 * @returns {Object} - { validate, errors, isValid, freelancerProfile, kycGateReady }
 */
export const useFreelancerValidation = (isFreelancer = false) => {
  // React Compiler : évite useMemoCache / dispatcher null (invalid hook call).
  'use no memo';
  const { t } = useTranslation();
  const { data: freelancerProfile, isFetched: freelancerProfileFetched } = useFreelancerProfile(isFreelancer);

  /** Profil freelance chargé au moins une fois — évite d’ouvrir YouSign / autosign avant le KYC réel */
  const kycGateReady = !isFreelancer || freelancerProfileFetched;

  const validate = useCallback(() => {
    if (!isFreelancer) {
      return { isValid: true, errors: { missingDocuments: [], missingIban: false } };
    }
    if (!freelancerProfileFetched) {
      return {
        isValid: false,
        errors: { missingDocuments: [], missingIban: false },
        pendingProfile: true,
      };
    }
    if (!freelancerProfile) {
      return {
        isValid: false,
        errors: { missingDocuments: [], missingIban: false },
        profileMissing: true,
      };
    }

    const errors = {
      missingDocuments: [],
      missingIban: false,
    };

    const isFrance = freelancerProfile.country === 'France';
    const isCompany = freelancerProfile.legal_entity_type === 'company';
    const isAutoEntrepreneur = freelancerProfile.legal_entity_type === 'auto-entrepreneur';
    const isPortage = freelancerProfile.legal_entity_type === 'portage-salarial';

    const existingDocuments = freelancerProfile.legal_documents || [];

    if (isPortage) {
      const portageCountry = freelancerProfile.portage_company?.country ?? freelancerProfile.country;
      const portageDocs = (freelancerProfile.portage_company_legal_documents || []).map((doc) => ({
        ...doc,
        category: normalizePortageKycCategory(doc.category),
      }));
      const requiredCategories = getRequiredPortageKycCategories(portageCountry, {
        requireRcInsurance: requiresRcInsurance(freelancerProfile),
      });
      const byCategory = new Map(portageDocs.map((doc) => [doc.category, doc]));
      const categoryLabels = {
        'registre-entreprises': (portageCountry || 'FR').toUpperCase() === 'FR'
          ? 'KBIS'
          : t('freelancerOnboarding.step4.registrationDocument'),
        'attestation-vigilance': t('freelancerOnboarding.step4.attestationVigilance'),
        'rc-insurance': t('freelancerOnboarding.step4.rcInsurance'),
      };
      requiredCategories.forEach((category) => {
        const doc = byCategory.get(category);
        const hasFile = Boolean(doc?.file_key?.trim() || doc?.file_name?.trim());
        if (!hasFile) {
          errors.missingDocuments.push(categoryLabels[category] || category);
        } else if (doc?.valid_until) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const exp = new Date(doc.valid_until);
          exp.setHours(0, 0, 0, 0);
          if (exp < today) {
            errors.missingDocuments.push(categoryLabels[category] || category);
          }
        }
      });

      const kycComplete = isKYCComplete(freelancerProfile);
      return {
        isValid: kycComplete && errors.missingDocuments.length === 0,
        errors: { ...errors, missingIban: false },
      };
    }

    const findDocument = (category) =>
      existingDocuments.find(doc => doc && doc.category === category);

    const isDocumentValid = (doc) => {
      if (!doc) return false;
      if (doc.validation_status === 'rejected') return false;
      return true;
    };

    const isAttestationValid = (doc) => {
      if (!doc) return false;
      if (doc.validation_status === 'rejected') return false;
      // Self-attestation on honor signed via Yousign — only counts once approved.
      const isSelfAttestation =
        doc.verification_data &&
        doc.verification_data.kind === 'self_attestation_non_remuneration';
      if (isSelfAttestation && doc.validation_status !== 'approved') {
        return false;
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (doc.valid_date) {
        const exp = new Date(doc.valid_date);
        exp.setHours(0, 0, 0, 0);
        return exp >= today;
      }
      if (doc.created_at) {
        const exp = new Date(doc.created_at);
        exp.setMonth(exp.getMonth() + 6);
        exp.setHours(0, 0, 0, 0);
        return exp >= today;
      }
      return false;
    };

    const requiredDocuments = [];
    const requireRc = requiresRcInsurance(freelancerProfile);
    const requireNda = requiresNdaActivityReceipt(freelancerProfile);
    const requireTaxRegularity = requiresTaxRegularityAttestation(freelancerProfile);
    const requireDriver = requiresDriverLicense(freelancerProfile);
    const requireCasier = requiresCriminalRecord(freelancerProfile);

    if (isFrance && isAutoEntrepreneur) {
      requiredDocuments.push(
        { category: 'registre-entreprises', label: t('freelancerOnboarding.step4.rne', 'RNE'), checkValidity: false },
        { category: 'attestation-vigilance', label: t('freelancerOnboarding.step4.attestationVigilance', 'Attestation de vigilance'), checkValidity: true },
        { category: 'scanned-id-card', label: t('freelancerOnboarding.step4.scannedId', 'Pièce d\'identité'), checkValidity: false }
      );
      if (requireRc) {
        requiredDocuments.push({
          category: 'rc-insurance',
          label: t('freelancerOnboarding.step4.rcInsurance'),
          rcInsurance: true,
        });
      }
      if (requireNda) {
        requiredDocuments.push({
          category: 'nda-activity-receipt',
          label: t('freelancerOnboarding.step4.ndaActivityReceipt'),
          ndaReceipt: true,
        });
      }
      if (requireTaxRegularity) {
        requiredDocuments.push({
          category: 'tax-regularity-attestation',
          label: t('freelancerOnboarding.step4.taxRegularityAttestation'),
          taxRegularityAttestation: true,
        });
      }
      if (requireDriver) {
        requiredDocuments.push({
          category: DRIVER_LICENSE_FRONT_CATEGORY,
          label: t('freelancerOnboarding.step4.driverLicenseFront'),
          driverLicenseFront: true,
        });
        requiredDocuments.push({
          category: DRIVER_LICENSE_BACK_CATEGORY,
          label: t('freelancerOnboarding.step4.driverLicenseBack'),
          driverLicenseBack: true,
        });
      }
      if (requireCasier) {
        requiredDocuments.push({
          category: 'casier-judiciaire',
          label: t('freelancerOnboarding.step4.criminalRecord'),
          criminalRecord: true,
        });
      }
    } else if (isFrance && isCompany) {
      requiredDocuments.push(
        { category: 'registre-entreprises', label: 'KBIS', checkValidity: false },
        { category: 'attestation-vigilance', label: t('freelancerOnboarding.step4.attestationVigilance', 'Attestation de vigilance'), checkValidity: true },
        { category: 'scanned-id-card', label: t('freelancerOnboarding.step4.scannedId', 'Pièce d\'identité'), checkValidity: false }
      );
      if (requireRc) {
        requiredDocuments.push({
          category: 'rc-insurance',
          label: t('freelancerOnboarding.step4.rcInsurance'),
          rcInsurance: true,
        });
      }
      if (requireNda) {
        requiredDocuments.push({
          category: 'nda-activity-receipt',
          label: t('freelancerOnboarding.step4.ndaActivityReceipt'),
          ndaReceipt: true,
        });
      }
      if (requireTaxRegularity) {
        requiredDocuments.push({
          category: 'tax-regularity-attestation',
          label: t('freelancerOnboarding.step4.taxRegularityAttestation'),
          taxRegularityAttestation: true,
        });
      }
      if (requireDriver) {
        requiredDocuments.push({
          category: DRIVER_LICENSE_FRONT_CATEGORY,
          label: t('freelancerOnboarding.step4.driverLicenseFront'),
          driverLicenseFront: true,
        });
        requiredDocuments.push({
          category: DRIVER_LICENSE_BACK_CATEGORY,
          label: t('freelancerOnboarding.step4.driverLicenseBack'),
          driverLicenseBack: true,
        });
      }
      if (requireCasier) {
        requiredDocuments.push({
          category: 'casier-judiciaire',
          label: t('freelancerOnboarding.step4.criminalRecord'),
          criminalRecord: true,
        });
      }
    } else {
      requiredDocuments.push(
        { category: 'scanned-id-card', label: t('freelancerOnboarding.step4.scannedId', 'Pièce d\'identité'), checkValidity: false },
        { category: 'registre-entreprises', label: t('freelancerOnboarding.step4.registrationDocument', 'Document d\'immatriculation'), checkValidity: false }
      );
      if (requireRc) {
        requiredDocuments.push({
          category: 'rc-insurance',
          label: t('freelancerOnboarding.step4.rcInsurance'),
          rcInsurance: true,
        });
      }
      if (requireDriver) {
        requiredDocuments.push({
          category: DRIVER_LICENSE_FRONT_CATEGORY,
          label: t('freelancerOnboarding.step4.driverLicenseFront'),
          driverLicenseFront: true,
        });
        requiredDocuments.push({
          category: DRIVER_LICENSE_BACK_CATEGORY,
          label: t('freelancerOnboarding.step4.driverLicenseBack'),
          driverLicenseBack: true,
        });
      }
    }

    requiredDocuments.forEach(({ category, label, checkValidity, rcInsurance, ndaReceipt, taxRegularityAttestation, criminalRecord, driverLicenseFront, driverLicenseBack }) => {
      const doc = findDocument(category);
      if (rcInsurance) {
        if (!isRcInsuranceValid(doc)) {
          errors.missingDocuments.push(label);
        }
      } else if (ndaReceipt) {
        if (!isNdaActivityReceiptValid(doc)) {
          errors.missingDocuments.push(label);
        }
      } else if (taxRegularityAttestation) {
        if (!isTaxRegularityAttestationValid(doc)) {
          errors.missingDocuments.push(label);
        }
      } else if (criminalRecord) {
        if (!isCriminalRecordValid(doc)) {
          errors.missingDocuments.push(label);
        }
      } else if (driverLicenseFront) {
        if (!isDriverLicenseDocumentValid(doc)) {
          errors.missingDocuments.push(label);
        }
      } else if (driverLicenseBack) {
        if (!isDriverLicenseBackDocumentValid(doc)) {
          errors.missingDocuments.push(label);
        }
      } else if (checkValidity) {
        if (!isAttestationValid(doc)) {
          errors.missingDocuments.push(label);
        }
      } else if (category === 'scanned-id-card') {
        if (!hasValidIdentityProof(freelancerProfile)) {
          errors.missingDocuments.push(label);
        }
      } else {
        if (!isDocumentValid(doc)) {
          errors.missingDocuments.push(label);
        }
      }
    });

    // CV / portfolio / diplômes (aucune vérification : présent et non rejeté suffit)
    if (requiresCv(freelancerProfile) && !hasValidCv(existingDocuments)) {
      errors.missingDocuments.push(t('freelancerOnboarding.step4.cv'));
    }
    if (requiresPortfolio(freelancerProfile) && !hasValidPortfolio(existingDocuments)) {
      errors.missingDocuments.push(t('freelancerOnboarding.step4.portfolio'));
    }
    if (requiresDiplomas(freelancerProfile) && !hasValidDiplomas(existingDocuments)) {
      errors.missingDocuments.push(t('freelancerOnboarding.step4.diplomas'));
    }

    // Titre de séjour (hors-UE travaillant depuis la France)
    if (requiresWorkLocationAnswer(freelancerProfile) && !hasAnsweredWorkLocation(freelancerProfile)) {
      errors.missingDocuments.push(t('freelancerSettings.documents.residencePermit.questionUnanswered'));
    } else if (
      requiresResidencePermit(freelancerProfile) &&
      !isResidencePermitValid(findDocument('residence-permit'))
    ) {
      errors.missingDocuments.push(t('freelancerSettings.documents.residencePermit.label'));
    }

    // Vérifier les informations bancaires (IBAN pour SEPA, ou informations internationales)
    const paymentZone = freelancerProfile.payment_zone || 'sepa';
    let hasBankInfo = false;

    if (paymentZone === 'sepa') {
      const ibanOk =
        freelancerProfile.iban &&
        freelancerProfile.iban !== 'NOT_PROVIDED' &&
        String(freelancerProfile.iban).trim() !== '';
      hasBankInfo = ibanOk && validateSwiftBic(freelancerProfile.swift_bic);
    } else if (paymentZone === 'international') {
      // Pour international, vérifier selon le pays
      const bankCountry = freelancerProfile.bank_country;
      
      if (bankCountry === 'US') {
        // USA : account_number, routing_number, account_type
        hasBankInfo = !!(freelancerProfile.account_number && 
                        freelancerProfile.routing_number && 
                        freelancerProfile.account_type);
      } else if (bankCountry === 'GB') {
        // UK : account_number, sort_code
        hasBankInfo = !!(freelancerProfile.account_number && 
                        freelancerProfile.sort_code);
      } else if (bankCountry === 'CA') {
        // Canada : account_number, transit_number, institution_number
        hasBankInfo = !!(freelancerProfile.account_number && 
                        freelancerProfile.transit_number && 
                        freelancerProfile.institution_number);
      } else if (bankCountry === 'AU') {
        // Australie : account_number, bsb_code
        hasBankInfo = !!(freelancerProfile.account_number && 
                        freelancerProfile.bsb_code);
      } else if (bankCountry === 'OTHER') {
        // Autre pays : account_number, swift_bic, bank_name
        hasBankInfo = !!(freelancerProfile.account_number && 
                        freelancerProfile.swift_bic && 
                        freelancerProfile.bank_name);
      }
    }

    errors.missingIban = !hasBankInfo;

    return {
      isValid: errors.missingDocuments.length === 0 && !errors.missingIban,
      errors,
    };
  }, [isFreelancer, freelancerProfile, freelancerProfileFetched, t]);

  const validationResult = validate();

  return {
    validate,
    errors: validationResult.errors,
    isValid: validationResult.isValid,
    freelancerProfile,
    kycGateReady,
  };
};

